import React, {FC, useEffect, useState} from 'react';
import closeIcon from 'assets/svg/close-icon.svg';
import closeImage from 'assets/svg/close.svg';
import Styles from 'components/chatContent/messageThread/MessageThread.module.scss';
import ChatFooter from 'components/chatContent/chatFooter/ChatFooter';
import MessageBox from 'components/chatContent/messageBox/MessageBox';
import { IMAGE_URL } from 'store/services/URL';
import {getImages, useGetThreadMessages} from "../../../store/services/firebase-broadcast";
import { formatMessageWithLineBreaks } from 'helpers/utils';

interface MessageThreadProps {
  hideReplies: Function;
  item: any;
  selected: any;
  actions: boolean;
  isDrawerOpen?: boolean;
}

const MessageThread: FC<MessageThreadProps> = (props: MessageThreadProps) => {
  const { hideReplies, item, selected, actions, isDrawerOpen } = props;
  const [typing, setTyping] = useState(false);
  const [attachment, setAttachment] = useState('');
  const [images, setImages] = useState<any[]>([]); // Add images state for attachments
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const handleShowReplies = () => hideReplies(false);

  // Use the custom hook for thread messages
  const { threads, isLoading } = useGetThreadMessages(selected?.pk, item?.id);

  const getTime = (time: string) => {
    const timeStr = time.split(':');
    return timeStr[0] + ':' + timeStr[1];
  };

  useEffect(() => {
    if (item.attachments?.length > 0 && item.attachments[0]?.id) {
      getImages(selected.pk, item.attachments[0].id).then((e) => setAttachment(e[0])).catch((error) => {
        // Silently handle missing attachment files
        console.warn('Failed to load thread attachment:', error);
      });
    }
  }, []);

  // Add functions to handle image attachments
  const handleImages = (newImage: any) => {
    setImages([...images, newImage]);
  };

  const clearImages = () => {
    setImages([]);
  };

  const removeImage = (imageId: string) => {
    setImages(images.filter(img => img.id !== imageId));
  };

  const onMessageSent = () => {
    // Scroll to bottom or refresh thread messages if needed
  };

  return (
    <div className={`${Styles.messageThreadContainer} ${isDrawerOpen ? Styles.drawerOpen : ''}`}>
      <div className={Styles.messageThreadWrapper}>
        <div className={Styles.header}>
          <h6 className="my-0 font-weight-normal fontSixteen text-black">Message thread</h6>
          <div onClick={handleShowReplies} onKeyPress={handleShowReplies} role="button" tabIndex={0}>
            <img src={closeIcon} alt="close icon" />
          </div>
        </div>
      <div className={`customScrollBar ${Styles.BodyContent}`}>
        <div className={`mb-0 ${Styles.message} ${Styles.borderBottom}`}>
          <img src={`${IMAGE_URL}${item.sender.image}`} alt="user" className={Styles.userImage} />
          <div className={Styles.userDetails}>
            <div className={Styles.detailsInner}>
              <h5 className="my-0">{item.sender.name}</h5>
              <span className="fontFifteen font-weight-normal input-color">{getTime(item.time)}</span>
              {item.sender.isHost && <span className={`badge text-white ${Styles.hostBadge}`}>Host</span>}
            </div>
            <p 
              className="fontFifteen text-black font-weight-normal mb-0"
              dangerouslySetInnerHTML={{ __html: formatMessageWithLineBreaks(item.message) }}
            />
            {attachment && (
              <div className="d-flex flex-column">
                <span className={Styles.uploadedImageDetails}>{item.attachments[0].name}</span>
                <img src={attachment} alt="attachment" className={Styles.uploadedImage} />
              </div>
            )}
          </div>
        </div>
        <p className="fontFourteen text-black font-weight-600 my-3">{threads.length} REPLIES</p>
        {threads.map((i, index) => (
          <MessageBox 
            item={i} 
            type="threads" 
            actions={false}  // Disable actions for thread replies
            selected={selected}  // Pass room context for permissions
            isThread={true}
            parentMessageId={item.id}
            threadIndex={index}
            key={i.id || `${i.timestamp || i.createdTime}-${i.sender?.pk || index}`} 
          />
        ))}
      </div>
      
      {/* Add attachment preview display - exact copy from ChatContent.tsx */}
      {images.length > 0 && (
        <div className={Styles.attachedMedia}>
          {images.map((i: any, index: number) => (
            <div
              className={`position-relative ${Styles.attachedInnerMedia}`}
              key={i.id || `image-${index}`}
            >
              {i.type === "video" ? (
                <video
                  width="320"
                  height="240"
                  className={Styles.attachedFile}
                  controls
                >
                  <source src={i.path} type="video/mp4" />
                </video>
              ) : (
                <img
                  src={i.path}
                  className={Styles.attachedFile}
                  alt="attached-file"
                />
              )}
              <div className={Styles.closeIconWrapper}
                onClick={() => removeImage(i.id)}>
                <img src={closeImage} alt="close-icon" />
              </div>
            </div>
          ))}
        </div>
      )}
      
      {typing ?
        <span className={`px-3 fontFifteen input-color font-weight-normal ${Styles.typingUser}`}>{`${user.name} is typing…`}</span>
        : (<div className="pb-2">&nbsp;</div>)}
        <ChatFooter 
          selected={selected} 
          isThread 
          parentMessage={item} 
          typing={setTyping}
          images={handleImages}
          clearImages={clearImages}
          attachedImages={images}
          onMessageSent={onMessageSent}
        />
      </div>
    </div>
  );
};
export default MessageThread;
